/**
 * Branding store (Pinia).
 *
 * Responsibilities:
 *   1. Hold the active branding payload for the current domain.
 *   2. Apply CSS variables + Vuetify theme tokens to <html> instantly.
 *   3. Persist a small subset to localStorage so the next page load
 *      paints with the right brand BEFORE the API responds (anti-FOUC).
 *   4. Bootstrap from cache on app start.
 *   5. Per-user theme override merge.
 *   6. Custom CSS overlay <link> swap.
 *
 * NOTE: this store is INTENTIONALLY non-blocking. If the API is slow or
 * the user is offline, the cached colors keep showing. Failure modes are
 * always "render with fallback Navy theme" � never blank.
 */
import { defineStore } from 'pinia'
import apiClient from '@/api/client'
import { setPlatformBarsColor } from '@/native/systemBars'
import {
  parseHex as _parseHex,
  contrastRatio as _contrastRatio,
  readableOn as _readableOn,
} from '@/utils/color'
import { resolveHostBrand } from '@/config/hostBrands'
import {
  reapplyActiveInterfaceVuetifyTheme,
  restoreActiveInterfaceVuetifyThemeBase,
} from '@/composables/interfaceThemeVuetifyBridge'
import { reapplyActiveInterfaceAppearance } from '@/composables/interfaceAppearanceBridge'

const LS_KEY = 'branding_v1'
// APPEARANCE: per-USER look. Two independent layers, both stored in
// users.ui_preferences (synced across devices via PATCH /auth/me/):
//   - icon_style:  'flat' | 'duotone' | 'color3d'   (which menu icon set)
//   - button_skin: 'classic' | 'premium'            (glossy surfaces/buttons)
// localStorage['ui_skin_v1'] stays as the fast anti-FOUC mirror of button_skin
// ('premium' | 'default'), used by the index.html boot script for <html data-skin>.
const LS_SKIN_KEY = 'ui_skin_v1'
// localStorage['icon_style_v1'] is the fast anti-FOUC mirror of icon_style. It
// survives a refreshUser() that momentarily returns a `user` object WITHOUT the
// icon_style key (legacy DB row / race on boot), so the menu never flashes back
// to the default set before the account pref re-applies. DB stays source-of-truth.
const LS_ICON_KEY = 'icon_style_v1'

// 'paper' = layered paper-cut set (light + dark variants), added 2026-07-19.
// 'newdesign' = Iconly-Pro two-tone set from the new CRM Figma design (light +
// dark variants), added 2026-09-11. See docs/FIGMA_DESIGN_MAP.md.
const ICON_STYLES = ['flat', 'duotone', 'color3d', 'paper', 'newdesign']

// Vuetify instance holder. main.js calls setVuetify(vuetify) at bootstrap so
// that branding refreshes triggered OUTSIDE main.js (e.g. on a company switch
// from the auth store) can still regenerate Vuetify's theme stylesheet, not
// just the raw CSS variables.
let _vuetifyRef = null

// Resolve the domain id of the currently authenticated company. Used to key the
// branding cache so the OLD company's logo/colors can never paint after a switch.
function _currentDomainId() {
  try {
    const raw = localStorage.getItem('domain') || sessionStorage.getItem('domain')
    if (raw) {
      const d = JSON.parse(raw)
      return d && d.id != null ? d.id : null
    }
  } catch {/* ignore */}
  return null
}

function _readUiPrefs() {
  try {
    const raw = localStorage.getItem('user') || sessionStorage.getItem('user')
    if (raw) return JSON.parse(raw)?.ui_preferences || null
  } catch {/* ignore */}
  return null
}

// Button skin from account pref, deriving from the legacy ui_skin when the new
// granular key is absent (existing users). Falls back to the juicy default.
function _readButtonSkin() {
  const p = _readUiPrefs()
  if (p) {
    if (p.button_skin === 'premium' || p.button_skin === 'classic') return p.button_skin
    if (p.ui_skin === 'premium') return 'premium'
    if (p.ui_skin === 'default') return 'classic'
  }
  try {
    const v = localStorage.getItem(LS_SKIN_KEY)
    if (v === 'premium') return 'premium'
    if (v === 'default') return 'classic'
  } catch {/* ignore */}
  return 'premium'
}

// Personal ("Just me") color theme CODE from account prefs. Stored in
// ui_preferences.personal_theme (NOT user_settings.theme_pack_id — that column
// is a FK to domain_branding_packs, a different concept). null = follow domain.
function _readPersonalTheme() {
  const p = _readUiPrefs()
  const code = p && p.personal_theme
  return (typeof code === 'string' && code) ? code : null
}

// Icon style from account pref, deriving from the legacy ui_skin when absent.
// Fallback chain: account pref (DB-backed cache) -> fast mirror (last known
// good) -> legacy ui_skin derivation -> default. The mirror step is what stops
// the menu from flashing back to the default set right after refreshUser() when
// the freshly fetched `user` object has no icon_style key yet.
let _iconStyleLogged = false
function _readIconStyle() {
  const p = _readUiPrefs()
  let mirror = null
  try { mirror = localStorage.getItem(LS_ICON_KEY) } catch {/* ignore */}
  // Style already committed to <html> by the index.html boot script BEFORE the
  // bundle ran (it reads the same account pref / mirror synchronously). Trust it
  // so the store's very first value matches the frame index.html already painted.
  let dom = null
  try { dom = document.documentElement.dataset.iconStyle } catch {/* ignore */}
  let out, src
  if (p && ICON_STYLES.includes(p.icon_style)) { out = p.icon_style; src = 'ui_preferences' }
  else if (ICON_STYLES.includes(dom)) { out = dom; src = 'dom' }
  else if (ICON_STYLES.includes(mirror)) { out = mirror; src = 'mirror' }
  else if (p && p.ui_skin === 'premium') { out = 'color3d'; src = 'legacy_ui_skin' }
  else if (p && p.ui_skin === 'default') { out = 'duotone'; src = 'legacy_ui_skin' }
  else { out = 'color3d'; src = 'default' }
  // Keep the fast persistent mirror in lockstep with a CONFIDENT source (the
  // account cache or the pre-boot DOM signal). Without this, a later
  // refreshUser() that returns a profile WITHOUT the icon_style key would let
  // the next first paint fall back to the default set and flash. Never persist a
  // mere default/legacy guess — that would poison the mirror.
  if ((src === 'ui_preferences' || src === 'dom') && out) {
    try { if (mirror !== out) localStorage.setItem(LS_ICON_KEY, out) } catch {/* ignore */}
  }
  if (!_iconStyleLogged) {
    _iconStyleLogged = true
    // console.warn (NOT log): production build strips console.log/info, so use
    // warn to keep this boot diagnostic visible on dev/prod for debugging the
    // "icons flash to default then re-apply" report.
    console.warn('[Branding] _readIconStyle →', out, '(from', src + ')',
      '| pref.icon_style=', p ? p.icon_style : '(no user cache)',
      '| mirror=', mirror, '| dom=', dom, '| ui_skin=', p ? p.ui_skin : undefined)
  }
  return out
}
const FALLBACK = {
  app_name: 'FiveStarJob',
  app_short_name: 'FSJ',
  app_tagline: '',
  logo_url: null,
  logo_dark_url: null,
  favicon_url: null,
  login_bg_url: null,
  light_primary: '#1e3a5f',
  light_accent: '#4dd4ac',
  light_background: '#f5f7fa',
  light_surface: '#ffffff',
  light_success: '#4dd4ac',
  light_warning: '#ff9966',
  light_error: '#ef4444',
  light_info: '#3b82f6',
  light_on_primary: '#ffffff',
  light_on_accent: '#ffffff',
  light_text_primary: '#1f2937',
  light_text_secondary: '#6b7280',
  dark_enabled: true,
  dark_primary: '#3b82f6',
  dark_accent: '#4dd4ac',
  dark_background: '#0f172a',
  dark_surface: '#1e293b',
  dark_text_primary: '#f1f5f9',
  dark_text_secondary: '#94a3b8',
  // Extended dark tokens. Not (yet) configurable per-domain via the branding
  // API — these defaults keep the full palette consistent in dark mode
  // (must mirror the `dark` theme in plugins/vuetify.js).
  dark_surface_variant: '#283548',
  dark_success: '#34d399',
  dark_warning: '#fbbf24',
  dark_error: '#f87171',
  dark_info: '#60a5fa',
  dark_on_primary: '#ffffff',
  dark_on_accent: '#06281e',
  font_family_primary: 'Inter',
  font_family_mono: 'Roboto Mono',
  font_google_imports: null,
  base_font_size: 14,
  font_scale: 1.0,
  border_radius_sm: 6,
  border_radius_md: 12,
  border_radius_lg: 16,
  density: 'comfortable',
  button_shape: 'rounded',
  card_elevation: 'soft',
  animation_preset: 'smooth',
  animation_duration_ms: 200,
  animation_easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
  pwa_theme_color: '#1e3a5f',
  pwa_background_color: '#f5f7fa',
  allow_user_dark_toggle: true,
  allow_user_density_override: false,
  allow_user_theme_pick: false,
  allow_user_font_scale: true,
  version: 1,
}

// Generic CSS font keywords that are NOT loadable webfonts (and must never be
// requested from Google Fonts, nor left as the app's only body font).
const _GENERIC_FONTS = new Set([
  'system-ui', 'ui-sans-serif', 'ui-serif', 'ui-monospace', 'ui-rounded',
  'sans-serif', 'serif', 'monospace', 'cursive', 'fantasy',
  '-apple-system', 'blinkmacsystemfont', 'inherit', 'initial', 'unset',
])

// First real (non-generic) family name from a CSS font stack, e.g.
// "Inter, system-ui, sans-serif" -> "Inter"; "JetBrains Mono, monospace" ->
// "JetBrains Mono". Returns '' when only generics are present.
function _firstFontFamily(stack) {
  if (!stack) return ''
  for (const raw of String(stack).split(',')) {
    const name = raw.trim().replace(/^['"]|['"]$/g, '')
    if (name && !_GENERIC_FONTS.has(name.toLowerCase())) return name
  }
  return ''
}

// Guarantee the applied brand font always ends in a SAFE sans-serif generic so
// the app can never fall back to the ugly UA serif/monospace default when a
// custom webfont fails to load. Keeps the configured family first.
function _safeFontStack(stack) {
  const first = _firstFontFamily(stack) || 'Inter'
  return `${first}, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`
}

function _hexToRgb(hex) {
  if (!hex) return '0,0,0'
  const m = String(hex).trim().replace('#', '')
  if (m.length !== 6) return '0,0,0'
  const r = parseInt(m.slice(0, 2), 16)
  const g = parseInt(m.slice(2, 4), 16)
  const b = parseInt(m.slice(4, 6), 16)
  return `${r},${g},${b}`
}

// --- WCAG auto-contrast --------------------------------------------------
// The theme is composed from three layers (Vuetify defaults, per-domain
// branding, per-user personal theme). Each layer used to set the SURFACE /
// BACKGROUND / PRIMARY / ACCENT colors WITHOUT touching their paired text
// (on-*) token, so a mismatched brand color produced "white text on a white
// surface". Best practice (Material 3 / WCAG): the TEXT color is always
// derived from the luminance of its background so it can never become
// invisible. These helpers implement that pairing.
const _TEXT_LIGHT = '#f1f5f9'  // near-white body text (dark surfaces)

// DARK-MODE READABILITY of the BRAND hue used as FOREGROUND.
// `primary`/`accent` are used both as a FILL (button background, with an on-*
// text token) AND as FOREGROUND (Vuetify `text-primary`, colored icons/links,
// `color="primary"`). A dark brand hue (e.g. A+'s navy #1e3a5f) works as a fill
// but is invisible as text on the dark surface (#1e293b) — "dark on dark". The
// dark theme was designed to use a LIGHTER blue for exactly this reason
// (see plugins/vuetify.js: "navy is too dark on dark"), but a per-domain or
// personal brand override reintroduces the dark hue WITHOUT that safeguard.
// This lifts the hue toward white until it clears a contrast floor against the
// dark surface — never darkens (dark mode wants lighter accents). Hues that
// already pass (e.g. #3b82f6) are returned unchanged, so well-configured
// domains and the light theme are never affected.
function _liftForDark(hex, surfaceHex, min = 3.2) {
  if (!_parseHex(hex) || !_parseHex(surfaceHex)) return hex
  if (_contrastRatio(surfaceHex, hex) >= min) return hex
  let [r, g, b] = _parseHex(hex)
  for (let i = 0; i < 24; i++) {
    r = Math.round(r + (255 - r) * 0.10)
    g = Math.round(g + (255 - g) * 0.10)
    b = Math.round(b + (255 - b) * 0.10)
    const lifted = '#' + [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')
    if (_contrastRatio(surfaceHex, lifted) >= min) return lifted
  }
  return _TEXT_LIGHT
}

// God Mode tabs (sessionStorage flag) must NOT share the branding cache with
// the admin's main tab: reading it would paint the ADMIN's brand inside the
// impersonated session, and writing it would overwrite the admin's cached
// brand with the impersonated tenant's one. God Mode sessions always load
// branding fresh from the API (per the impersonated JWT's domain).
function _isGodModeTab() {
  try { return !!sessionStorage.getItem('god_mode') } catch { return false }
}

function _readCache() {
  try {
    if (_isGodModeTab()) return null
    const raw = localStorage.getItem(LS_KEY)
    if (!raw) return null
    const slim = JSON.parse(raw)
    // Never paint a cached brand that belongs to a DIFFERENT company than the
    // one we're authenticated into now — that's exactly the "old logo lingers
    // after switching companies" bug. Only enforced when both ids are known;
    // on the login page (no domain yet) the cache is kept for anti-FOUC.
    const cur = _currentDomainId()
    if (cur != null && slim.dom != null && Number(slim.dom) !== Number(cur)) return null
    return slim
  } catch { return null }
}

function _writeCache(payload) {
  try {
    if (_isGodModeTab()) return
    // Keep cache small � only the visual subset
    const slim = {
      v: payload.version || 1,
      // light
      lp: payload.light_primary, la: payload.light_accent,
      lb: payload.light_background, ls: payload.light_surface,
      ltp: payload.light_text_primary, lts: payload.light_text_secondary,
      // dark
      de: payload.dark_enabled, dp: payload.dark_primary, da: payload.dark_accent,
      db: payload.dark_background, ds: payload.dark_surface,
      dtp: payload.dark_text_primary, dts: payload.dark_text_secondary,
      // identity
      app: payload.app_name, short: payload.app_short_name,
      logo: payload.logo_url, logod: payload.logo_dark_url,
      fav: payload.favicon_url,
      // typo + layout
      ff: payload.font_family_primary, fs: payload.base_font_size,
      fg: payload.font_google_imports,
      rsm: payload.border_radius_sm, rmd: payload.border_radius_md, rlg: payload.border_radius_lg,
      den: payload.density, btn: payload.button_shape,
      ce: payload.card_elevation,
      // pwa
      pt: payload.pwa_theme_color, pb: payload.pwa_background_color,
      // permissions
      udt: payload.allow_user_dark_toggle, udo: payload.allow_user_density_override,
      utp: payload.allow_user_theme_pick, ufs: payload.allow_user_font_scale,
    }
    // Preserve the effective theme mode written by applyToDom (anti-FOUC for dark)
    try {
      const prev = JSON.parse(localStorage.getItem(LS_KEY) || '{}')
      if (prev.mode) slim.mode = prev.mode
    } catch {/* noop */}
    // Stamp the owning company so a cache from company A is never reused for B.
    slim.dom = _currentDomainId()
    localStorage.setItem(LS_KEY, JSON.stringify(slim))
  } catch {/* quota or private mode � ignore */}
}

// Effective theme mode kept in the slim cache by applyToDom. index.html paints
// the page with it before any JS runs, but the store used to start with
// theme_mode_override = null, i.e. `isDark` was false until /account/my-theme/
// answered. Everything keyed off it (the sidebar colour above all) therefore
// rendered its LIGHT variant for the first few hundred milliseconds on a dark
// account and then snapped back — the "sidebar briefly changes its look after a
// reload" report. Seeding the mode from the same cache the boot script uses
// keeps HTML and Vue in agreement on the first frame; the server value replaces
// it as soon as it arrives. TICK-00061.
function _readCachedMode() {
  const slim = _readCache()
  // Only 'dark' is seeded: light is what an absent override resolves to anyway,
  // so a light account keeps a genuinely empty override object.
  return slim && slim.mode === 'dark' ? 'dark' : null
}

function _expandCache(slim) {
  if (!slim) return null
  return {
    light_primary: slim.lp || FALLBACK.light_primary,
    light_accent: slim.la || FALLBACK.light_accent,
    light_background: slim.lb || FALLBACK.light_background,
    light_surface: slim.ls || FALLBACK.light_surface,
    light_text_primary: slim.ltp || FALLBACK.light_text_primary,
    light_text_secondary: slim.lts || FALLBACK.light_text_secondary,
    dark_enabled: !!slim.de, dark_primary: slim.dp, dark_accent: slim.da,
    dark_background: slim.db, dark_surface: slim.ds,
    dark_text_primary: slim.dtp, dark_text_secondary: slim.dts,
    app_name: slim.app, app_short_name: slim.short,
    logo_url: slim.logo, logo_dark_url: slim.logod, favicon_url: slim.fav,
    font_family_primary: slim.ff || FALLBACK.font_family_primary,
    font_google_imports: slim.fg,
    base_font_size: slim.fs || FALLBACK.base_font_size,
    border_radius_sm: slim.rsm || FALLBACK.border_radius_sm,
    border_radius_md: slim.rmd || FALLBACK.border_radius_md,
    border_radius_lg: slim.rlg || FALLBACK.border_radius_lg,
    density: slim.den || FALLBACK.density,
    button_shape: slim.btn || FALLBACK.button_shape,
    card_elevation: slim.ce || FALLBACK.card_elevation,
    pwa_theme_color: slim.pt || FALLBACK.pwa_theme_color,
    pwa_background_color: slim.pb || FALLBACK.pwa_background_color,
    allow_user_dark_toggle: !!slim.udt,
    allow_user_density_override: !!slim.udo,
    allow_user_theme_pick: !!slim.utp,
    allow_user_font_scale: !!slim.ufs,
    version: slim.v || 1,
  }
}

export const useBrandingStore = defineStore('branding', {
  state: () => ({
    current: { ...FALLBACK, ..._expandCache(_readCache()) },
    userOverride: {
      theme_pack_id: null,
      // 'light' | 'dark' | 'system' | null — seeded from the anti-FOUC cache so
      // the first Vue frame already knows a dark account is dark (TICK-00061).
      theme_mode_override: _readCachedMode(),
      density_override: null,
      font_scale_override: null,
    },
    flags: {
      allow_user_dark_toggle: true,
      allow_user_density_override: false,
      allow_user_theme_pick: false,
      allow_user_font_scale: true,
    },
    themes: [],
    history: [],
    loading: false,
    customCssEnabled: false,
    customCssVersion: 0,
    bootstrapped: false,
    // APPEARANCE: two independent per-user look layers (see _read* helpers).
    iconStyle: _readIconStyle(),     // 'flat' | 'duotone' | 'color3d'
    buttonSkin: _readButtonSkin(),   // 'classic' | 'premium'
    // Personal ("Just me") color theme override resolved from the user's
    // theme_pack_id. null = follow domain branding. Shape: { primary, accent,
    // background, surface } (single set, applied to the active mode).
    userThemeColors: null,
    // Cache of available themes (with tokens) from /account/my-theme, used to
    // resolve theme_pack_id -> colors on boot and on pick.
    accountThemes: [],
    // Effective allowance for THIS user to pick a personal theme. True when the
    // domain enables it OR the user holds `branding.user_override` (admins).
    canPickTheme: false,
    // Personal theme CODE (e.g. 'ocean_blue') persisted in ui_preferences.
    // null = follow domain branding.
    personalThemeCode: _readPersonalTheme(),
  }),

  getters: {
    // Back-compat alias: many components key their juicy treatment off this.
    // The juicy surfaces/buttons are the "premium" BUTTON skin.
    isPremiumSkin(state) { return state.buttonSkin === 'premium' },
    // Full-color 3D menu icons are the "color3d" ICON style.
    isColorIcons(state) { return state.iconStyle === 'color3d' },
    // Layered paper-cut ICON style (light + dark asset sets).
    isPaperIcons(state) { return state.iconStyle === 'paper' },
    // Iconly-Pro two-tone ICON style from the new CRM Figma design (light + dark).
    isNewDesignIcons(state) { return state.iconStyle === 'newdesign' },
    // Icon styles that render rich raster PNGs (bigger glyphs) vs mono mdi.
    // NOTE: 'newdesign' is intentionally NOT rich — its Figma icons are small,
    // clean menu glyphs, so they use a dedicated small size (see App.vue) and no
    // glow, matching the design 1:1.
    isRichIcons(state) { return state.iconStyle === 'color3d' || state.iconStyle === 'paper' },
    effectiveMode(state) {
      const m = state.userOverride.theme_mode_override
      if (m === 'dark' || m === 'light') return m
      if (m === 'system' && window?.matchMedia) {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
      }
      return 'light'
    },
    isDark() { return this.effectiveMode === 'dark' },
  },

  actions: {
    /** Remember the Vuetify instance so switch-triggered reloads (from the auth
     *  store) can regenerate the theme stylesheet, not just the CSS vars. */
    setVuetify(v) { _vuetifyRef = v },

    /**
     * Wipe ALL company-specific branding from memory + storage so nothing from
     * the previous tenant lingers after a logout / company switch: logo, colors,
     * app name, favicon, cached HTML brand slots and any personal overrides.
     * The next loadCurrent()/loadPublic() repaints for the new company.
     */
    reset(vuetifyInstance = null) {
      this.current = { ...FALLBACK }
      this.flags = {
        allow_user_dark_toggle: true,
        allow_user_density_override: false,
        allow_user_theme_pick: false,
        allow_user_font_scale: true,
      }
      this.themes = []
      this.history = []
      this.customCssEnabled = false
      this.userThemeColors = null
      this.personalThemeCode = null
      this.accountThemes = []
      this.bootstrapped = false
      try { localStorage.removeItem(LS_KEY) } catch {/* ignore */}
      try { if (window.__brandSlotsCache) window.__brandSlotsCache = {} } catch {/* ignore */}
      this.applyToDom(vuetifyInstance || _vuetifyRef)
    },

    /** Apply colors + fonts + layout vars to <html>. Cheap, idempotent. */
    applyToDom(vuetifyInstance = null) {
      const c = this.current
      if (!c) return
      const root = document.documentElement
      const mode = this.effectiveMode
      vuetifyInstance = vuetifyInstance || _vuetifyRef

      // Vuetify's scoped `.v-theme--light` / `.v-theme--dark` declarations
      // must return to the branding-owned base before that base is rebuilt.
      restoreActiveInterfaceVuetifyThemeBase(root, vuetifyInstance)

      const setVar = (name, value) => { if (value !== undefined && value !== null) root.style.setProperty(name, value) }

      // Persist the effective mode into the slim cache so the index.html
      // anti-FOUC script paints DARK immediately on the next page load
      // (otherwise dark users get a white flash on every reload).
      try {
        if (!_isGodModeTab()) {
          const raw = localStorage.getItem(LS_KEY)
          if (raw) {
            const slim = JSON.parse(raw)
            if (slim.mode !== mode) {
              slim.mode = mode
              localStorage.setItem(LS_KEY, JSON.stringify(slim))
            }
          }
        }
      } catch {/* noop */}

      // Vuetify-compatible variables: rgb triplets
      if (mode === 'dark' && c.dark_enabled) {
        // Lift a too-dark brand hue so it is readable as FOREGROUND text/icons
        // on the dark surface (text-primary, color="primary"). Fills stay brand,
        // just lighter; on-* below are derived from the lifted hue so buttons
        // keep readable labels. Well-configured light hues pass through unchanged.
        const dp = _liftForDark(c.dark_primary, c.dark_surface)
        const da = _liftForDark(c.dark_accent, c.dark_surface)
        setVar('--v-theme-primary',     _hexToRgb(dp))
        setVar('--v-theme-accent',      _hexToRgb(da))
        setVar('--v-theme-background',  _hexToRgb(c.dark_background))
        setVar('--v-theme-surface',     _hexToRgb(c.dark_surface))
        setVar('--v-theme-surface-variant', _hexToRgb(c.dark_surface_variant || FALLBACK.dark_surface_variant))
        // Full token set: without these, success/warning/error/info and on-*
        // keep their LIGHT values in dark mode (washed-out chips, invisible text).
        setVar('--v-theme-success',     _hexToRgb(c.dark_success || FALLBACK.dark_success))
        setVar('--v-theme-warning',     _hexToRgb(c.dark_warning || FALLBACK.dark_warning))
        setVar('--v-theme-error',       _hexToRgb(c.dark_error   || FALLBACK.dark_error))
        setVar('--v-theme-info',        _hexToRgb(c.dark_info    || FALLBACK.dark_info))
        // on-* text tokens: always derived from their real (lifted) background so
        // text stays readable whatever brand colors the domain configured.
        setVar('--v-theme-on-primary',  _hexToRgb(_readableOn(dp, c.dark_on_primary || FALLBACK.dark_on_primary, 3)))
        setVar('--v-theme-on-accent',   _hexToRgb(_readableOn(da,  c.dark_on_accent  || FALLBACK.dark_on_accent, 3)))
        setVar('--v-theme-on-surface',  _hexToRgb(_readableOn(c.dark_surface, c.dark_text_primary)))
        setVar('--v-theme-on-background', _hexToRgb(_readableOn(c.dark_background, c.dark_text_primary)))
        setVar('--brand-text-primary', c.dark_text_primary)
        setVar('--brand-text-secondary', c.dark_text_secondary)
      } else {
        setVar('--v-theme-primary',     _hexToRgb(c.light_primary))
        setVar('--v-theme-accent',      _hexToRgb(c.light_accent))
        setVar('--v-theme-background',  _hexToRgb(c.light_background))
        setVar('--v-theme-surface',     _hexToRgb(c.light_surface))
        setVar('--v-theme-surface-variant', _hexToRgb('#eef2f7'))
        setVar('--v-theme-success',     _hexToRgb(c.light_success))
        setVar('--v-theme-warning',     _hexToRgb(c.light_warning))
        setVar('--v-theme-error',       _hexToRgb(c.light_error))
        setVar('--v-theme-info',        _hexToRgb(c.light_info))
        // on-* text tokens: always derived from their real background so text
        // stays readable whatever brand colors the domain configured.
        setVar('--v-theme-on-primary',  _hexToRgb(_readableOn(c.light_primary, c.light_on_primary, 3)))
        setVar('--v-theme-on-accent',   _hexToRgb(_readableOn(c.light_accent,  c.light_on_accent, 3)))
        setVar('--v-theme-on-surface',  _hexToRgb(_readableOn(c.light_surface, c.light_text_primary)))
        setVar('--v-theme-on-background', _hexToRgb(_readableOn(c.light_background, c.light_text_primary)))
        setVar('--brand-text-primary',  c.light_text_primary)
        setVar('--brand-text-secondary', c.light_text_secondary)
      }

      // PERSONAL THEME ("Just me"): override the main palette with the user's
      // picked theme tokens, WITHOUT changing domain branding. Applied to the
      // active mode (single token set). Cleared theme => no override.
      const utc = this.userThemeColors
      if (utc) {
        // Personal color themes carry a single (light-oriented) token set. In
        // DARK mode we must NOT let the theme's light background/surface repaint
        // over the dark ones — that produced "picked a Color Theme, then dark
        // mode won't apply, fixed only by Reset" (TICK-00145). In dark mode we
        // keep the dark background/surface and only borrow the brand HUE
        // (primary/accent). In light mode the full set applies as before.
        // Each overridden background repaints its paired text token too, so a
        // personal theme can never introduce a white-on-white surface.
        const darkActive = mode === 'dark' && c.dark_enabled
        // In dark mode the personal hue is borrowed as-is; a dark picked hue
        // (e.g. navy) would be invisible as text on the dark surface, so lift it
        // to a readable value exactly like the domain path above.
        if (utc.primary) {
          const up = darkActive ? _liftForDark(utc.primary, c.dark_surface) : utc.primary
          setVar('--v-theme-primary', _hexToRgb(up))
          setVar('--v-theme-on-primary', _hexToRgb(_readableOn(up, null, 3)))
        }
        if (utc.accent) {
          const ua = darkActive ? _liftForDark(utc.accent, c.dark_surface) : utc.accent
          setVar('--v-theme-accent', _hexToRgb(ua))
          setVar('--v-theme-on-accent', _hexToRgb(_readableOn(ua, null, 3)))
        }
        if (!darkActive && utc.background) {
          setVar('--v-theme-background', _hexToRgb(utc.background))
          setVar('--v-theme-on-background', _hexToRgb(_readableOn(utc.background, null)))
        }
        if (!darkActive && utc.surface) {
          setVar('--v-theme-surface', _hexToRgb(utc.surface))
          setVar('--v-theme-on-surface', _hexToRgb(_readableOn(utc.surface, null)))
        }
      }

      // Layout + animation tokens (custom � only consumed by components opted in)
      setVar('--brand-radius-sm', `${c.border_radius_sm}px`)
      setVar('--brand-radius-md', `${c.border_radius_md}px`)
      setVar('--brand-radius-lg', `${c.border_radius_lg}px`)
      // Always keep a safe sans-serif fallback tail so a failed/exotic webfont
      // can never drop the whole app into the UA serif/monospace default.
      setVar('--brand-font-primary', _safeFontStack(c.font_family_primary))
      setVar('--brand-font-base-size', `${c.base_font_size || 14}px`)
      const scale = parseFloat(this.userOverride.font_scale_override || c.font_scale || 1)
      setVar('--brand-font-scale', String(scale))
      setVar('--brand-anim-duration', `${c.animation_duration_ms || 200}ms`)
      setVar('--brand-anim-easing', c.animation_easing || 'cubic-bezier(0.4, 0, 0.2, 1)')

      // Density + theme attribute (lets CSS hooks switch themes)
      const density = this.userOverride.density_override || c.density || 'comfortable'
      root.dataset.density = density
      root.dataset.theme = mode
      root.dataset.brandVersion = String(c.version || 1)

      // BUTTON-SKIN: reassert the data-skin attribute so the scoped premium CSS
      // (glossy surfaces, buttons, header) applies. Driven by the button skin
      // layer now; index.html sets it pre-boot, we keep it in sync after hydration.
      if (this.buttonSkin === 'premium') root.dataset.skin = 'premium'
      else delete root.dataset.skin
      // ICON-STYLE: exposed for CSS hooks (icon rendering itself is JS in App.vue).
      root.dataset.iconStyle = this.iconStyle

      // <title> — the standalone dialer popup owns its title ("FiveStarJob
      // Dialer" + live call state, set by DialerWindowView); never override it.
      // On the sign-in page of a tenant's own domain (pageonelife.com) the tab
      // must carry THAT brand: no company is known yet, so `app_name` is still
      // the platform default. Once signed in, the tenant's app_name takes over.
      if (!window.location.pathname.startsWith('/dialer')) {
        const signInBrand = window.location.pathname.startsWith('/login')
          ? resolveHostBrand()?.name
          : null
        const title = signInBrand || c.app_name
        if (title) {
          try { document.title = title } catch {/* noop */}
        }
      }

      // <meta name="theme-color"> (browser status bar) — follows active mode
      try {
        const meta = document.querySelector('meta[name="theme-color"]')
        const themeColor = (mode === 'dark' && c.dark_enabled)
          ? (c.dark_background || FALLBACK.dark_background)
          : c.pwa_theme_color
        if (meta && themeColor) meta.setAttribute('content', themeColor)
      } catch {/* noop */}

      // Native system bars (no-op on web). Both app bars — the office one and
      // the technician header — are painted with the SURFACE colour, and so is
      // the bottom of every screen, so it is the right reference for the status
      // and navigation bars. A technician interface preset that paints its own
      // header overrides the status bar from TechnicianApp.vue.
      try {
        const barSurface = (mode === 'dark' && c.dark_enabled)
          ? c.dark_surface
          : ((utc && utc.surface) || c.light_surface)
        setPlatformBarsColor(barSurface)
      } catch {/* noop */}

      // Favicon swap (only when value differs from current).
      // The dialer popup keeps its own green phone icon — never swap it there.
      try {
        if (c.favicon_url && !window.location.pathname.startsWith('/dialer')) {
          const link = document.querySelector('link[rel="icon"]') || document.createElement('link')
          link.rel = 'icon'
          link.href = c.favicon_url
          if (!link.parentNode) document.head.appendChild(link)
        }
      } catch {/* noop */}

      // Live update Vuetify if instance provided (for instant theme color rebuild).
      // Accept either the full vuetify plugin (has `.theme`) OR a `useTheme()` proxy (has `.themes` directly).
      try {
        const themeProxy = vuetifyInstance?.theme || vuetifyInstance
        if (themeProxy && themeProxy.themes?.value) {
          const themeName = mode === 'dark' && c.dark_enabled ? 'dark' : 'light'
          const themesRef = themeProxy.themes
          const current = themesRef.value[themeName] || { dark: themeName === 'dark', colors: {} }
          const newColors = { ...(current.colors || {}) }
          if (mode === 'dark' && c.dark_enabled) {
            if (c.dark_primary)    newColors.primary    = c.dark_primary
            if (c.dark_accent)     newColors.accent     = c.dark_accent
            if (c.dark_accent)     newColors.secondary  = c.dark_accent
            if (c.dark_background) newColors.background = c.dark_background
            if (c.dark_surface)    newColors.surface    = c.dark_surface
          } else {
            if (c.light_primary)    newColors.primary    = c.light_primary
            if (c.light_accent)     newColors.accent     = c.light_accent
            if (c.light_accent)     newColors.secondary  = c.light_accent
            if (c.light_background) newColors.background = c.light_background
            if (c.light_surface)    newColors.surface    = c.light_surface
            if (c.light_success)    newColors.success    = c.light_success
            if (c.light_warning)    newColors.warning    = c.light_warning
            if (c.light_error)      newColors.error      = c.light_error
            if (c.light_info)       newColors.info       = c.light_info
          }
          // PERSONAL THEME override (matches the CSS-var override above).
          // In dark mode keep the dark background/surface (only borrow the hue)
          // so a light personal theme can't wash out dark mode (TICK-00145).
          if (this.userThemeColors) {
            const u = this.userThemeColors
            const darkActive = mode === 'dark' && c.dark_enabled
            if (u.primary)    newColors.primary    = u.primary
            if (u.accent)   { newColors.accent = u.accent; newColors.secondary = u.accent }
            if (!darkActive && u.background) newColors.background = u.background
            if (!darkActive && u.surface)    newColors.surface    = u.surface
          }
          // Re-pair the on-* text tokens with whatever backgrounds ended up
          // applied, so Vuetify's generated stylesheet matches the CSS vars
          // set above and text is guaranteed readable (no white-on-white).
          const preferredText = (mode === 'dark' && c.dark_enabled) ? c.dark_text_primary : c.light_text_primary
          if (newColors.surface)    newColors['on-surface']    = _readableOn(newColors.surface, preferredText)
          if (newColors.background) newColors['on-background'] = _readableOn(newColors.background, preferredText)
          if (newColors.primary)    newColors['on-primary']    = _readableOn(newColors.primary, null, 3)
          if (newColors.accent)   { newColors['on-accent'] = _readableOn(newColors.accent, null, 3); newColors['on-secondary'] = newColors['on-accent'] }
          // Replace the whole themes ref with a new object so Vuetify regenerates its style sheet.
          themesRef.value = {
            ...themesRef.value,
            [themeName]: { ...current, colors: newColors },
          }
          if (themeProxy.global?.name) themeProxy.global.name.value = themeName
        }
      } catch (e) { console.warn('[Branding] Vuetify live update skipped:', e?.message) }

      // Custom CSS overlay link
      try {
        const linkId = 'brand-custom-css'
        let link = document.getElementById(linkId)
        if (this.customCssEnabled) {
          if (!link) {
            link = document.createElement('link')
            link.id = linkId
            link.rel = 'stylesheet'
            document.head.appendChild(link)
          }
          // Need a domain id for ?domain_id=
          let domainId = null
          try { domainId = JSON.parse(localStorage.getItem('user') || '{}').current_domain_id } catch {/* */}
          link.href = `/api/v1/branding/custom-css/raw?domain_id=${domainId || ''}&v=${this.customCssVersion}`
        } else if (link) {
          link.remove()
        }
      } catch (e) { console.warn('[Branding] custom CSS overlay skipped:', e?.message) }

      // Google Fonts load (one-shot per font_family value).
      // ROBUST: request ONLY the first real family name. Google Fonts 400s on a
      // full comma stack (e.g. "Inter, system-ui, sans-serif"), which used to
      // leave the whole app on the UA fallback font. We also skip generic
      // keywords so we never fire a broken/empty request.
      try {
        const first = _firstFontFamily(c.font_family_primary)
        if (first && !document.querySelector(`link[data-brand-font="${first}"]`)) {
          const lk = document.createElement('link')
          lk.rel = 'stylesheet'
          lk.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(first)}:wght@400;500;600;700&display=swap`
          lk.dataset.brandFont = first
          document.head.appendChild(lk)
        }
      } catch {/* noop */}

      // Domain branding and the personal theme are the base palette. When the
      // technician shell has registered an active interface palette, it owns
      // final precedence and must be reasserted after every base write.
      reapplyActiveInterfaceVuetifyTheme(root, vuetifyInstance)
      reapplyActiveInterfaceAppearance(root)
    },

    async loadCurrent(vuetifyInstance = null) {
      // Fall back to the bootstrap Vuetify instance when called without one
      // (e.g. from the auth store right after a company switch).
      vuetifyInstance = vuetifyInstance || _vuetifyRef
      this.loading = true
      try {
        const { data } = await apiClient.get('/branding/current/')
        this.current = { ...FALLBACK, ...data }
        this.flags = {
          allow_user_dark_toggle: !!data.allow_user_dark_toggle,
          allow_user_density_override: !!data.allow_user_density_override,
          allow_user_theme_pick: !!data.allow_user_theme_pick,
          allow_user_font_scale: !!data.allow_user_font_scale,
        }
        _writeCache(this.current)
        // Custom CSS state
        try {
          const css = await apiClient.get('/branding/custom-css/')
          this.customCssEnabled = !!css.data.is_enabled
          this.customCssVersion = (this.customCssVersion + 1)
        } catch {/* user may not have designer permission � ignore */}
        // User override + personal theme resolution
        try {
          const myt = await apiClient.get('/account/my-theme/')
          this.userOverride = myt.data.overrides || this.userOverride
          if (myt.data.allowed) this.flags = { ...this.flags, ...myt.data.allowed }
          this.canPickTheme = !!(myt.data.can_pick_theme ?? myt.data.allowed?.allow_user_theme_pick)
          this.accountThemes = myt.data.themes || []
          this.resolveUserTheme()
        } catch {/* */}
        this.applyToDom(vuetifyInstance)
        this.bootstrapped = true
      } catch (e) {
        const code = e?.response?.status
        if (code === 401 || code === 403 || code === 404) {
          // User has no branding.view permission (e.g. technician). Use public branding instead.
          try { await this.loadPublic(undefined, vuetifyInstance) } catch {/* keep cache */}
          // Still load the per-USER theme override (dark toggle, personal theme).
          // /account/my-theme/ is IsAuthenticated (no branding.view needed), so a
          // technician's saved Dark Mode choice is restored on every reload —
          // without this, dark reverted to light on refresh for techs (they never
          // reach the my-theme fetch in the success path above).
          try {
            const myt = await apiClient.get('/account/my-theme/')
            this.userOverride = myt.data.overrides || this.userOverride
            if (myt.data.allowed) this.flags = { ...this.flags, ...myt.data.allowed }
            this.canPickTheme = !!(myt.data.can_pick_theme ?? myt.data.allowed?.allow_user_theme_pick)
            this.accountThemes = myt.data.themes || []
            this.resolveUserTheme()
            this.applyToDom(vuetifyInstance)
          } catch {/* no domain context / offline — keep cache */}
        } else {
          console.warn('[Branding] loadCurrent failed:', e?.message)
          this.applyToDom(vuetifyInstance)
        }
      } finally {
        this.loading = false
      }
      return this.current
    },

    /** Public bootstrap (no-auth) � used on /login. Domain by subdomain or query. */
    async loadPublic(subdomain, vuetifyInstance = null) {
      try {
        const params = subdomain ? `?subdomain=${encodeURIComponent(subdomain)}` : ''
        const r = await fetch(`/api/v1/branding/public/${params}`, { credentials: 'omit' })
        if (r.ok) {
          const data = await r.json()
          this.current = { ...FALLBACK, ...data }
          _writeCache(this.current)
        }
      } catch {/* keep cache */}
      this.applyToDom(vuetifyInstance)
    },

    async patchCurrent(payload, vuetifyInstance = null) {
      const { data } = await apiClient.patch('/branding/current/', payload)
      this.current = { ...FALLBACK, ...data }
      _writeCache(this.current)
      this.applyToDom(vuetifyInstance)
      return data
    },

    async loadThemes() {
      const { data } = await apiClient.get('/branding/themes/')
      this.themes = data || []
      return this.themes
    },

    async applyTheme(themeId, vuetifyInstance = null) {
      const { data } = await apiClient.post(`/branding/themes/${themeId}/apply/`)
      if (data?.branding) {
        this.current = { ...FALLBACK, ...data.branding }
        // COMPANY apply replaces the whole look for everyone (incl. this admin):
        // drop any personal override so the new domain theme is fully applied
        // and no colors from a previously picked personal theme stick around.
        this.userThemeColors = null
        this.personalThemeCode = null
        _writeCache(this.current)
        this.applyToDom(vuetifyInstance)
      }
      return data
    },

    async loadHistory() {
      const { data } = await apiClient.get('/branding/history/')
      this.history = data || []
      return this.history
    },

    async rollback(version, vuetifyInstance = null) {
      const { data } = await apiClient.post(`/branding/rollback/${version}/`)
      if (data?.branding) {
        this.current = { ...FALLBACK, ...data.branding }
        _writeCache(this.current)
        this.applyToDom(vuetifyInstance)
      }
      return data
    },

    async setUserOverride(payload, vuetifyInstance = null) {
      const { data } = await apiClient.patch('/account/my-theme/', payload)
      this.userOverride = data.overrides || this.userOverride
      this.resolveUserTheme(vuetifyInstance)
      this.applyToDom(vuetifyInstance)
      return data
    },

    async resetUserOverride(vuetifyInstance = null) {
      await apiClient.post('/account/my-theme/reset/')
      this.userOverride = {
        theme_pack_id: null, theme_mode_override: null,
        density_override: null, font_scale_override: null,
      }
      this.userThemeColors = null
      this.personalThemeCode = null
      this.applyToDom(vuetifyInstance)
    },

    /** Re-read the per-user UI prefs (icon set, button skin, personal color
     *  theme) from the freshly refreshed `user` cache and apply them. Called on
     *  boot AFTER auth.refreshUser() + loadCurrent() so the look matches the
     *  account in the DB on EVERY device, independent of the local cache. */
    syncAccountPrefs(vuetifyInstance = null) {
      // Reassign ONLY when the value actually changed. Setting the same value
      // is a no-op for Vue reactivity, but guarding keeps intent explicit and
      // avoids any needless icon re-render churn after boot.
      const nextIcon = _readIconStyle()
      if (this.iconStyle !== nextIcon) this.iconStyle = nextIcon
      const nextSkin = _readButtonSkin()
      if (this.buttonSkin !== nextSkin) this.buttonSkin = nextSkin
      this.personalThemeCode = _readPersonalTheme()
      // Keep the fast anti-FOUC mirrors in sync for the next page load, so the
      // very first synchronous read paints the correct icon set + skin.
      try { localStorage.setItem(LS_SKIN_KEY, this.buttonSkin === 'premium' ? 'premium' : 'default') } catch {/* ignore */}
      try { localStorage.setItem(LS_ICON_KEY, this.iconStyle) } catch {/* ignore */}
      this.resolveUserTheme(vuetifyInstance)
      this.applyToDom(vuetifyInstance || _vuetifyRef)
      console.log('[Branding] syncAccountPrefs →', { icon: this.iconStyle, skin: this.buttonSkin, theme: this.personalThemeCode })
    },

    /** ICON-STYLE: set the menu icon set ('flat' | 'duotone' | 'color3d').
     *  Applies instantly; the caller persists to the account (ui_preferences). */
    setIconStyle(style, vuetifyInstance = null) {
      const next = ICON_STYLES.includes(style) ? style : 'color3d'
      if (this.iconStyle !== next) this.iconStyle = next
      // Write the fast mirror immediately so a reload right after picking paints
      // this set on the first frame (before refreshUser / syncAccountPrefs run).
      try { localStorage.setItem(LS_ICON_KEY, next) } catch {/* ignore */}
      this.applyToDom(vuetifyInstance)
      console.log('[Branding] iconStyle →', next)
    },

    /** BUTTON-SKIN: set the glossy surfaces/buttons layer ('classic'|'premium').
     *  Mirrors the legacy ui_skin anti-FOUC cache; caller persists to account. */
    setButtonSkin(skin, vuetifyInstance = null) {
      const next = skin === 'premium' ? 'premium' : 'classic'
      this.buttonSkin = next
      try { localStorage.setItem(LS_SKIN_KEY, next === 'premium' ? 'premium' : 'default') } catch {/* ignore */}
      this.applyToDom(vuetifyInstance)
      console.log('[Branding] buttonSkin →', next)
    },

    /** Back-compat: legacy single "juicy" toggle moved icons + buttons together.
     *  Kept so older callers (MyThemeView) keep working. */
    setSkin(skin, vuetifyInstance = null) {
      const premium = skin === 'premium'
      this.setIconStyle(premium ? 'color3d' : 'duotone', vuetifyInstance)
      this.setButtonSkin(premium ? 'premium' : 'classic', vuetifyInstance)
    },

    /** PERSONAL THEME: apply a theme's color tokens to the current user only
     *  (no domain change). `tokens` is the theme.tokens object. */
    setUserThemeColors(tokens, vuetifyInstance = null) {
      const color = (tokens && tokens.color) || null
      this.userThemeColors = color ? {
        primary: color.primary, accent: color.accent,
        background: color.background, surface: color.surface,
      } : null
      this.applyToDom(vuetifyInstance)
    },

    /** PERSONAL THEME ("Just me"): set the chosen color theme by CODE + apply
     *  its tokens instantly. The caller persists `personal_theme` into the
     *  account ui_preferences. Pass code=null + tokens=null to clear. */
    setPersonalTheme(code, tokens, vuetifyInstance = null) {
      this.personalThemeCode = code || null
      this.setUserThemeColors(tokens || null, vuetifyInstance)
    },

    /** Resolve the user's saved personal theme CODE -> color tokens (from the
     *  cached account themes) and apply them. Called on boot.
     *  IMPORTANT: when a code is set but its tokens are not in the cache, we DO
     *  NOT wipe already-applied personal colors (avoids a just-picked theme
     *  snapping back to the domain theme). */
    resolveUserTheme(vuetifyInstance = null) {
      const code = this.personalThemeCode
      if (!code) { this.userThemeColors = null; this.applyToDom(vuetifyInstance); return }
      const t = (this.accountThemes || []).find((x) => x.code === code)
      if (t && t.tokens) this.setUserThemeColors(t.tokens, vuetifyInstance)
      // else: keep current userThemeColors as-is (no revert).
    },

    async refreshCustomCss(vuetifyInstance = null) {
      try {
        const { data } = await apiClient.get('/branding/custom-css/')
        this.customCssEnabled = !!data.is_enabled
        this.customCssVersion += 1
        this.applyToDom(vuetifyInstance)
      } catch {/* */}
    },
  },
})
